/**
 * Certificate API - Optimized with caching
 */
import { academyApiHeaders, academyApiUrl } from '@/lib/constants/api';

export interface CertificateData {
  uid: string;
  title: string;
  userUid: {
    uid: string;
    firstName: string;
    lastName: string;
    email?: string;
  };
  learningPathId?: string;
  createdAt: string;
  updatedAt: string;
}

// Simple in-memory cache
const cache = new Map<string, { data: CertificateData | CertificateData[] | null; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// In-flight requests by key so duplicate calls (e.g. React Strict Mode) share one network request
const inFlight = new Map<string, Promise<unknown>>();

/** `not-found` is a definitive 404 (safe to render a 404 page); `error` is transient and must NOT be shown as "doesn't exist". */
export type CertificateFetchResult<T> =
  | { status: 'ok'; data: T }
  | { status: 'not-found' }
  | { status: 'error' };

// Generic fetch with caching and request deduplication
async function fetchCertificateResult<T>(
  key: string,
  url: string
): Promise<CertificateFetchResult<T>> {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return { status: 'ok', data: cached.data as T };
  }

  const existing = inFlight.get(key);
  if (existing) {
    return existing as Promise<CertificateFetchResult<T>>;
  }

  const promise = (async (): Promise<CertificateFetchResult<T>> => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'include',
        headers: academyApiHeaders({ 'Content-Type': 'application/json' }),
      });

      // 404 is the only status that definitively means "no such certificate"
      if (response.status === 404) return { status: 'not-found' };
      if (!response.ok) return { status: 'error' };

      const data = await response.json();
      cache.set(key, { data, timestamp: Date.now() });
      return { status: 'ok', data };
    } catch {
      return { status: 'error' };
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

// Back-compat wrapper: collapses every failure mode to null
async function fetchCertificate<T>(key: string, url: string): Promise<T | null> {
  const result = await fetchCertificateResult<T>(key, url);
  return result.status === 'ok' ? result.data : null;
}

// Public endpoint (404s rather than requiring a session) so shared certificate links work for anonymous visitors.
export async function getCertificateResultByID(
  certificateID: string
): Promise<CertificateFetchResult<CertificateData>> {
  return fetchCertificateResult<CertificateData>(
    `cert-${certificateID}`,
    academyApiUrl(`/v1/users/achievements/certificate/${encodeURIComponent(certificateID)}`)
  );
}

export async function getCertificateDataByID(
  certificateID: string
): Promise<CertificateData | null> {
  const result = await getCertificateResultByID(certificateID);
  return result.status === 'ok' ? result.data : null;
}

// Get certificate by learning path ID (matches Academy 1.0: GET .../certificate?learningPathId=...)
export async function getCertificateDataByLpID(
  learningPathID: string
): Promise<CertificateData | null> {
  const data = await fetchCertificate<CertificateData | { certificates?: CertificateData[] }>(
    `cert-lp-${learningPathID}`,
    academyApiUrl(`/v1/users/achievements/certificate?learningPathId=${encodeURIComponent(learningPathID)}`)
  );
  if (!data) return null;
  if (Array.isArray((data as { certificates?: CertificateData[] }).certificates)) {
    const certs = (data as { certificates: CertificateData[] }).certificates;
    return certs.find((c) => c.learningPathId === learningPathID) ?? certs[0] ?? null;
  }
  return data as CertificateData;
}

// Clear cache (useful for testing or after mutations)
export function clearCertificateCache() {
  cache.clear();
}
